import React, { useEffect, useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { adminApiService } from "../../../utils/api";
import { messages } from "../../../locales/messages";
import {
  GfImage,
  GfImageTag,
  UpdateGfImageDto,
} from "@swagger/typescript-fetch-goflyer";
import { useUploadOrPickImages } from "../../../hooks/useUploadOrPickImages";
import { useImageTagOptions } from "../../../hooks";

import { SetupPageContainer } from "../../../utils/commonStyledComponents/commonStyled";
import { AccountCardWrapper } from "../../components/AccountCardWrapper";
import { CustomTextField } from "./styled";
import {
  DragElement,
  DragInputElement,
} from "../CreateDealTab/StyledComponents";
import { CustomUploadLabelCard } from "../CreateUploadFlyer/styled";
import SelectMui from "../../components/SelectMui";
import { useLoading } from "../../../contextApi/LoadingContext";
import { MuiConfirmationModal } from "../../components/MuiConfirmationModal";
import { toastError, toastSuccess } from "../../../utils/pageUtil/toast";
import { PermissionComponentWrapper } from "../../components/PermissionComponentWrapper";
import { useAuthHeaders } from "../../../hooks/useAuthHeaders";
import { disableWheelOnInput } from "../../../utils/disableWheelOnInput";
import { CustomAutocompleteMultiSelect } from "../../components/CustomAutocomplete";
import { routes } from "../../../utils/constant/navigationRoutes";

const S3_PATH_OPTIONS = [
  // { label: "Flyers Folder", value: "flyers" },
  { label: "Product Images Folder", value: "productImages" },
];

const CreateImagePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { imageId } = useParams<{ imageId?: string }>();
  const { showLoadingScreen } = useLoading();
  const authHeaders = useAuthHeaders();
  const { imageTagOptionList } = useImageTagOptions();

  const [imageDetails, setImageDetails] = useState<UpdateGfImageDto>();
  const [originalImageDetails, setOriginalImageDetails] =
    useState<UpdateGfImageDto>();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewImageUrl, setPreviewImageUrl] = useState<string>("");
  const [imageName, setImageName] = useState<string>("");
  const [fileExtension, setFileExtension] = useState<string>("");
  const [s3Folder, setS3Folder] = useState<string>("productImages");
  const [open, setOpen] = useState(false);
  const [createImageTags, setCreateImageTags] = useState<GfImageTag[]>([]);

  const { handleOnImageFileSelect } = useUploadOrPickImages({
    onPickedImageChange: async (images: GfImage[]) => {
      const latestImage = images[images.length - 1];
      if (!latestImage) {
        showLoadingScreen(false);
        return;
      }
      if (createImageTags.length > 0) {
        try {
          await adminApiService.updateOneBaseGfImageAdminControllerGfImage(
            { imageTags: createImageTags },
            latestImage.id,
          );
        } catch (error) {
          console.error("Error saving image tags:", error);
        }
      }
      navigate(routes.image.editById(latestImage.id));
    },
  });

  const handleOpenDelete = () => {
    setOpen(true);
  };

  const handleCancel = () => {
    setOpen(false);
  };
  const handleDeleteImage = async () => {
    if (imageDetails) {
      try {
        showLoadingScreen(true);
        await adminApiService.gfImageAdminControllerSoftDelete(
          { ids: [imageDetails.id as string] },
          authHeaders,
        );

        handleCancel();
        toastSuccess(t(messages.Successfully_Deleted()));
        showLoadingScreen(false);

        navigate(-1);
      } catch (error) {
        console.log("error", error);
        showLoadingScreen(false);
        toastError(
          t(messages.There_is_an_error_please_Contact_Administrator()),
        );
      }
    }
  };

  useEffect(() => {
    if (!imageId) return;
    showLoadingScreen(true);

    adminApiService
      .getOneBaseGfImageAdminControllerGfImage(imageId)
      .then((fetchedImage) => {
        setImageDetails({
          ...fetchedImage,
        });

        setOriginalImageDetails({
          ...fetchedImage,
        });
      })
      .catch((err) => {
        console.error("Error fetching image:", err);
        alert("Error loading image details");
      })
      .finally(() => {
        showLoadingScreen(false);
      });
  }, [imageId]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    setSelectedFile(file);

    const fileNameParts = file.name.split(".");
    if (fileNameParts.length > 1) {
      const extension = fileNameParts.pop() || "";
      setFileExtension(extension);
      const baseFileName = fileNameParts.join(".");
      setImageName(baseFileName);
    } else {
      setFileExtension("");
      setImageName(file.name);
    }
  };

  useEffect(() => {
    if (!selectedFile) {
      setPreviewImageUrl("");
      return;
    }
    const objectURL = URL.createObjectURL(selectedFile);
    setPreviewImageUrl(objectURL);
    return () => {
      URL.revokeObjectURL(objectURL);
    };
  }, [selectedFile]);

  const uploadSelectedFile = async () => {
    if (!selectedFile) return;
    if (!imageName) {
      toastError("Please provide a filename before uploading.");
      return;
    }
    showLoadingScreen(true);
    const completeFileName = fileExtension
      ? `${imageName}.${fileExtension}`
      : imageName;
    const renamedFile = new File([selectedFile], completeFileName, {
      type: selectedFile.type,
    });

    const updatedFile = {
      target: { files: [renamedFile] },
    } as unknown as React.ChangeEvent<HTMLInputElement>;

    await handleOnImageFileSelect(updatedFile, s3Folder);
  };

  const saveImageDetails = async () => {
    if (!imageDetails?.alt || !imageDetails?.src) {
      alert(
        `${t(messages.alt())} ${t(messages.is_Empty())} or ${t(
          messages.source(),
        )} ${t(messages.is_Empty())}`,
      );
      return;
    }

    try {
      if (imageDetails?.id) {
        await adminApiService.updateOneBaseGfImageAdminControllerGfImage(
          imageDetails,
          imageDetails?.id,
        );
      }
      navigate(-1);
    } catch (error) {
      console.error("Error saving image details:", error);
      alert("Failed to save image. Please try again.");
    }
  };

  const isEditingImage = Boolean(imageId);

  const generatedS3Key = !isEditingImage
    ? s3Folder && imageName
      ? fileExtension
        ? `${s3Folder}/${imageName}.${fileExtension}`
        : `${s3Folder}/${imageName}`
      : ""
    : "";

  return (
    <SetupPageContainer>
      <AccountCardWrapper
        title={
          isEditingImage
            ? `${t(messages.Edit_Image_Details())}`
            : `${t(messages.Create_New_Image())}`
        }
        data-testid="create-image-page-container"
      >
        <Box
          sx={{ maxWidth: 500, mx: "auto", mt: 4, mb: 4 }}
          data-testid="image-form-container"
        >
          {isEditingImage ? (
            <Stack spacing={3} data-testid="edit-image-form">
              {originalImageDetails?.src && (
                <Box
                  textAlign="center"
                  mb={2}
                  data-testid="original-image-preview-container"
                >
                  <Typography variant="body2" mb={1}>
                    {t(messages.Original_Image_Preview())}
                  </Typography>
                  <img
                    src={originalImageDetails?.src}
                    alt={originalImageDetails?.alt || "preview"}
                    style={{
                      maxWidth: 400,
                      border: "1px solid #ccc",
                      borderRadius: 4,
                    }}
                    data-testid="original-image-preview"
                  />
                </Box>
              )}
              {originalImageDetails?.srcSmall && (
                <Box
                  textAlign="center"
                  mb={2}
                  data-testid="small-image-preview-container"
                >
                  <Typography variant="body2" mb={1}>
                    {t(messages.Small_Image_Preview())}
                  </Typography>
                  <img
                    src={originalImageDetails?.srcSmall}
                    alt={originalImageDetails?.alt || "small preview"}
                    style={{
                      maxWidth: 300,
                      border: "1px solid #ccc",
                      borderRadius: 4,
                    }}
                    data-testid="small-image-preview"
                  />
                </Box>
              )}
              {originalImageDetails?.srcTinyPng && (
                <Box
                  textAlign="center"
                  mb={2}
                  data-testid="tiny-png-preview-container"
                >
                  <Typography variant="body2" mb={1}>
                    {t(messages.Tiny_PNG_Preview())}
                  </Typography>
                  <img
                    src={originalImageDetails?.srcTinyPng}
                    alt={originalImageDetails?.alt || "tiny PNG preview"}
                    style={{
                      maxWidth: 200,
                      border: "1px solid #ccc",
                      borderRadius: 4,
                    }}
                    data-testid="tiny-png-preview"
                  />
                </Box>
              )}

              <CustomTextField
                label={t(messages.alt())}
                value={imageDetails?.alt || ""}
                onChange={(e) =>
                  setImageDetails((prev) => ({ ...prev, alt: e.target.value }))
                }
                fullWidth
                data-testid="image-alt-input"
              />
              <CustomTextField
                label={t(messages.source())}
                value={imageDetails?.src || ""}
                onChange={(e) =>
                  setImageDetails((prev) => ({ ...prev, src: e.target.value }))
                }
                fullWidth
                data-testid="image-src-input"
              />
              <CustomTextField
                label={t(messages.sourceSmall())}
                value={imageDetails?.srcSmall || ""}
                onChange={(e) =>
                  setImageDetails((prev) => ({
                    ...prev,
                    srcSmall: e.target.value,
                  }))
                }
                fullWidth
                data-testid="image-src-small-input"
              />
              <CustomTextField
                label={t(messages.sourceTinyPng())}
                value={imageDetails?.srcTinyPng || ""}
                onChange={(e) =>
                  setImageDetails((prev) => ({
                    ...prev,
                    srcTinyPng: e.target.value,
                  }))
                }
                fullWidth
                data-testid="image-src-tiny-png-input"
              />
              <CustomTextField
                type="number"
                onWheel={disableWheelOnInput}
                label={t(messages.width())}
                value={imageDetails?.width || ""}
                onChange={(e) =>
                  setImageDetails((prev) => ({
                    ...prev,
                    width: Number(e.target.value),
                  }))
                }
                fullWidth
                data-testid="image-width-input"
              />
              <CustomTextField
                type="number"
                onWheel={disableWheelOnInput}
                label={t(messages.height())}
                value={imageDetails?.height || ""}
                onChange={(e) =>
                  setImageDetails((prev) => ({
                    ...prev,
                    height: Number(e.target.value),
                  }))
                }
                fullWidth
                data-testid="image-height-input"
              />
              <CustomTextField
                label={t(messages.s3Size())}
                type="number"
                onWheel={disableWheelOnInput}
                value={imageDetails?.s3Size || ""}
                onChange={(e) =>
                  setImageDetails((prev) => ({
                    ...prev,
                    s3Size: parseInt(e.target.value),
                  }))
                }
                fullWidth
                data-testid="image-s3-size-input"
              />
              <CustomTextField
                type="number"
                onWheel={disableWheelOnInput}
                label={t(messages.splitRows())}
                value={imageDetails?.splitRows || ""}
                onChange={(e) =>
                  setImageDetails((prev) => ({
                    ...prev,
                    splitRows: Number(e.target.value),
                  }))
                }
                fullWidth
                data-testid="image-split-rows-input"
              />
              <CustomTextField
                type="number"
                onWheel={disableWheelOnInput}
                label={t(messages.splitCols())}
                value={imageDetails?.splitCols || ""}
                onChange={(e) =>
                  setImageDetails((prev) => ({
                    ...prev,
                    splitCols: Number(e.target.value),
                  }))
                }
                fullWidth
                data-testid="image-split-cols-input"
              />
              <Box>
                <Typography variant="body2" mb={0.5} color="text.secondary">
                  {t(messages.Image_Tag())}
                </Typography>
                <CustomAutocompleteMultiSelect
                  optionsList={imageTagOptionList?.map((tag) => ({
                    value: tag,
                    label: tag.name,
                  }))}
                  selectedValues={imageDetails?.imageTags?.map((tag) => ({
                    value: tag,
                    label: tag.name,
                  }))}
                  multiSelect
                  handleOnChange={(value: GfImageTag[] | undefined) =>
                    setImageDetails((prev) => ({
                      ...prev,
                      imageTags: value,
                    }))
                  }
                  WrapperCss="width: 100%; position: relative;"
                  inputCss="width: 100%;"
                  dataTestId="image-tag-select"
                />
              </Box>

              <Stack direction="row" justifyContent="space-between" mt={2}>
                <Stack direction="row" columnGap={2}>
                  <PermissionComponentWrapper
                    requiredPermission={["delete"]}
                    requiredPermissionGroup={["image"]}
                  >
                    <Button
                      variant="contained"
                      onClick={handleOpenDelete}
                      color="error"
                      data-testid="delete-image-button"
                    >
                      {t(messages.Delete())}
                    </Button>
                  </PermissionComponentWrapper>
                  <Button
                    variant="outlined"
                    onClick={() => navigate(-1)}
                    sx={{
                      borderColor: "#f58220",
                      color: "#000",
                      "&:hover": { borderColor: "#e4711f" },
                    }}
                    data-testid="cancel-button"
                  >
                    {t(messages.Cancel())}
                  </Button>
                </Stack>
                <Button
                  variant="contained"
                  onClick={saveImageDetails}
                  sx={{
                    backgroundColor: "#f58220",
                    "&:hover": { backgroundColor: "#e4711f" },
                  }}
                  data-testid="update-image-button"
                >
                  {t(messages.Update())}
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Box textAlign="center" mb={3} data-testid="create-image-form">
              {/* Upload Section */}
              <DragElement
                data-testid="image-upload-drop-zone"
                style={{
                  width: 200,
                  height: 200,
                  border: "2px dashed #f58220",
                  borderRadius: 8,
                  margin: "0 auto",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <DragInputElement
                  type="file"
                  id="upload"
                  accept="image/*"
                  multiple={false}
                  onChange={handleFileChange}
                  data-testid="image-file-input"
                />
                {!selectedFile && (
                  <CustomUploadLabelCard
                    htmlFor="upload"
                    data-testid="upload-label"
                  >
                    <AddIcon sx={{ fontSize: 40, color: "#f58220" }} />
                  </CustomUploadLabelCard>
                )}
                {selectedFile && (
                  <Box
                    textAlign="center"
                    sx={{ p: 1 }}
                    data-testid="file-preview-container"
                  >
                    <Typography
                      variant="body2"
                      sx={{ mb: 1 }}
                      data-testid="selected-file-name"
                    >
                      {selectedFile.name}
                    </Typography>
                    {previewImageUrl && (
                      <img
                        src={previewImageUrl}
                        alt="preview"
                        data-testid="selected-file-preview"
                        style={{
                          maxWidth: "80%",
                          maxHeight: 150,
                          border: "1px solid #ccc",
                          borderRadius: 4,
                        }}
                      />
                    )}
                  </Box>
                )}
              </DragElement>
              {selectedFile && (
                <Stack
                  direction="column"
                  spacing={2}
                  mt={2}
                  sx={{ alignItems: "center" }}
                >
                  <SelectMui
                    title="S3 Folder"
                    selectOptions={S3_PATH_OPTIONS}
                    selectedValue={s3Folder}
                    onSelect={(val) => {
                      setS3Folder(val);
                    }}
                    dataTestId="s3-folder-select"
                  />
                  <CustomTextField
                    label={t(messages.Filename_without_extension())}
                    value={imageName}
                    onChange={(e) => setImageName(e.target.value)}
                    fullWidth
                    data-testid="image-filename-input"
                  />
                  {generatedS3Key && (
                    <CustomTextField
                      label={t(messages.S3_Key_read_only())}
                      value={generatedS3Key}
                      fullWidth
                      disabled
                      data-testid="image-s3-key-readonly"
                    />
                  )}
                  <Box sx={{ width: "100%", textAlign: "left" }}>
                    <Typography variant="body2" mb={0.5} color="text.secondary">
                      {t(messages.Image_Tag())}
                    </Typography>
                    <CustomAutocompleteMultiSelect
                      optionsList={imageTagOptionList?.map((tag) => ({
                        value: tag,
                        label: tag.name,
                      }))}
                      selectedValues={createImageTags.map((tag) => ({
                        value: tag,
                        label: tag.name,
                      }))}
                      multiSelect
                      handleOnChange={(value: GfImageTag[] | undefined) =>
                        setCreateImageTags(value || [])
                      }
                      WrapperCss="width: 100%; position: relative;"
                      inputCss="width: 100%;"
                      dataTestId="create-image-tag-select"
                    />
                  </Box>
                  <Box>
                    <Button
                      variant="contained"
                      onClick={uploadSelectedFile}
                      sx={{
                        backgroundColor: "#f58220",
                        "&:hover": { backgroundColor: "#e4711f" },
                      }}
                      data-testid="upload-image-button"
                    >
                      {t(messages.Upload())}
                    </Button>
                  </Box>
                </Stack>
              )}
            </Box>
          )}
        </Box>
      </AccountCardWrapper>
      <MuiConfirmationModal
        open={open}
        handleCancel={handleCancel}
        handleConfirm={handleDeleteImage}
        title={`${t(messages.Delete_Confirmation())}`}
        description={`
              ${t(messages.Are_you_sure_you_want_to_delete_These())}?  ${t(
                messages.Image_soft_delete_notice(),
              )}
            `}
      />
    </SetupPageContainer>
  );
};

export default CreateImagePage;
